import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { QuestionData } from '../models'
import { Question } from '../components/Question'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Dialog from "@material-ui/core/Dialog";
import { submitQuiz, submissionCheck } from '../utils/api.utils'
import { Flex, PrimaryHeader, SecondaryHeader, PromptBody, PromptButton, PromptHeader, NavBackButton, BackIcon } from '../styles/global'

interface Props {
  quiz: any
  shouldDisplay: any
  fetchQuizzes: any
  onSave?: (quiz: any) => void  // Optional callback for in-memory updates
}

export function QuizEditor({ quiz, shouldDisplay, fetchQuizzes, onSave }: Props) {
  const [currentQuiz, setQuiz] = useState(quiz);
  const [questions, setQuestions] = useState<QuestionData[]>(currentQuiz.questions);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    setQuiz((targetQuiz: any) => ({ ...targetQuiz, questions: questions }))
  }, [questions]);

  const handleSave = () => {
    try {
      const validatedQuiz = submissionCheck(currentQuiz);
      if (!validatedQuiz) return;

      // If onSave callback is provided (unsaved simulation), use it
      if (onSave) {
        onSave(currentQuiz);
        return;
      }

      // Otherwise, save to backend (saved simulation)
      submitQuiz(validatedQuiz)
        .then(response => {
          fetchQuizzes();
          shouldDisplay(false);
          return response;
        })
        .catch(err => {
          console.error(err);
          setErrorMsg("There was an error saving the quiz. Please try again.");
          setShowErrorModal(true);
        });
    } catch (e: any) {
      setErrorMsg(e.message || "Please check all required fields.");
      setShowErrorModal(true);
    }
  };

  return (
    <EditorWrapper>
      <EditorHeader>
        <NavBackButton startIcon={<BackIcon />} onClick={() => shouldDisplay(false)}>
          Back to Quizzes
        </NavBackButton>
        <Flex justify="space-between" alignItems="center">
          <PrimaryHeader style={{ margin: 0 }}>{quiz.quiz_id === 0 ? "Create New Quiz" : "Edit Quiz"}</PrimaryHeader>
          <Flex>
            <ActionBtn onClick={() => shouldDisplay(false)} style={{ marginRight: '12px' }}>
              Discard Changes
            </ActionBtn>
            <ActionBtn $primary onClick={(e) => {
              (e.currentTarget as HTMLElement).blur();
              handleSave();
            }}>
              Save Quiz
            </ActionBtn>
          </Flex>
        </Flex>
      </EditorHeader>

      <SectionWrapper>
        <SecondaryHeader>Quiz Details</SecondaryHeader>
        <FormCard>
          <FieldGroup>
            <Label>Quiz Title *</Label>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="e.g. Basic Pathogen Knowledge"
              value={currentQuiz.title || ""}
              onChange={(e) => {
                const value = e.target.value
                setQuiz((current: any) => ({ ...current, title: value }))
              }}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Description *</Label>
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              placeholder="Provide a brief overview of what this quiz covers..."
              value={currentQuiz.description || ""}
              onChange={(e) => {
                const value = e.target.value
                setQuiz((current: any) => ({ ...current, description: value }))
              }}
            />
          </FieldGroup>
        </FormCard>
      </SectionWrapper>

      <SectionWrapper>
        <Flex justify="space-between" alignItems="center" style={{ marginBottom: '24px' }}>
          <SecondaryHeader style={{ margin: 0 }}>Questions ({questions.length})</SecondaryHeader>
          <Button
            variant="contained"
            color="primary"
            style={{ borderRadius: '8px', padding: '8px 20px', fontWeight: 600 }}
            onClick={() => {
              setQuestions((current: any) => [
                ...current,
                {
                  id: uuidv4(),
                  created_at: Date.now(),
                  title: "",
                  answer: "",
                  a: "",
                  b: "",
                  c: "",
                  d: "",
                  e: "",
                  f: "",
                }
              ]);
            }}
          >
            + Add Question
          </Button>
        </Flex>

        <QuestionsList>
          {questions.map((questionItem: QuestionData, i: number) => (
            <QuestionCard key={questionItem.id?.toString() ?? `question-${i}`}>
              <QuestionNumber>Question {i + 1}</QuestionNumber>
              <Question
                question={questionItem}
                updateQuestions={setQuestions}
              />
            </QuestionCard>
          ))}
        </QuestionsList>
      </SectionWrapper>

      <FooterActions justify="center">
        <Button
          variant="contained"
          color="primary"
          size="large"
          style={{ borderRadius: '8px', padding: '12px 48px', fontWeight: 700, fontSize: '16px' }}
          onClick={(e) => {
            (e.currentTarget as HTMLElement).blur();
            handleSave();
          }}
        >
          Save Quiz & Finish
        </Button>
      </FooterActions>

      <Dialog
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '8px',
            maxWidth: '450px'
          }
        }}
      >
        <ErrorModalContent direction="column" alignItems="center">
          <div className="error-icon">!</div>
          <PromptHeader style={{ color: '#1a202c', marginBottom: '12px', fontSize: '24px', fontWeight: 800 }}>
            Hold on a moment
          </PromptHeader>
          <PromptBody style={{ color: '#4a5568', textAlign: 'center', lineHeight: 1.6, fontSize: '15px' }}>
            {errorMsg}
          </PromptBody>
          <Flex style={{ marginTop: '32px', width: '100%' }} justify="center">
            <PromptButton
              autoFocus
              onClick={() => setShowErrorModal(false)}
              variant="contained"
              style={{
                background: '#3f51b5',
                color: 'white',
                borderRadius: '8px',
                padding: '10px 32px',
                fontWeight: 600,
                textTransform: 'none',
                width: '100%',
                boxShadow: '0 4px 12px rgba(63, 81, 181, 0.2)'
              }}
            >
              Okay, I'll fix it
            </PromptButton>
          </Flex>
        </ErrorModalContent>
      </Dialog>
    </EditorWrapper>
  );
}

const ErrorModalContent = styled(Flex)`
  padding: 32px;
  
  .error-icon {
    width: 48px;
    height: 48px;
    background: #fff5f5;
    color: #e53e3e;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 20px;
    border: 2px solid #feb2b2;
  }
`;

const EditorWrapper = styled.div`
  width: 100%;
  margin: 0;
  padding-bottom: 100px;
`;

const EditorHeader = styled.div`
  margin-bottom: 40px;
`;



const SectionWrapper = styled.div`
  margin-bottom: 48px;
`;

const FormCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const FieldGroup = styled.div`
  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px;
  position: relative;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const QuestionNumber = styled.div`
  position: absolute;
  top: 20px;
  right: 32px;
  font-size: 14px;
  font-weight: 700;
  color: #cbd5e0;
  text-transform: uppercase;
`;

const ActionBtn = styled(Button) <{ $primary?: boolean }>`
  && {
    border-radius: 8px;
    padding: 8px 16px;
    font-weight: 600;
    text-transform: none;
    ${props => props.$primary ? `
      background: #3f51b5;
      color: white;
      &:hover {
        background: #303f9f;
      }
    ` : `
      color: #718096;
      &:hover {
        background: #f7fafc;
      }
    `}
  }
`;

const FooterActions = styled(Flex)`
  margin-top: 64px;
  padding-top: 32px;
  border-top: 1px solid #e2e8f0;
`;
